/**
 * Product video strip — play the previews that are actually on screen. (Theme PRD F3, PR D.)
 *
 * The strip renders its videos with `preload="none"` and no `autoplay`, so out of the box nothing
 * downloads and nothing moves. This script is the whole of the "muted, looping preview" behaviour
 * the reference layout asks for, and it is deliberately the ONLY thing that starts playback:
 *
 *   - `autoplay` in the markup would start every video in the strip at once, on page load, on
 *     mobile data. A product can carry eight of them.
 *   - `preload="none"` only helps while nothing plays; autoplay overrides it by definition.
 *
 * So playback follows visibility: a card starts when enough of it is on screen and stops when it
 * leaves. If this file never loads, the videos still have controls and still play when tapped —
 * the page degrades to "a strip of videos", not to "a strip of stills".
 *
 * HOW MANY PLAY AT ONCE, precisely, because "one at a time" would be wrong and the difference is
 * the entire cost argument above. EVERY card that passes the visibility test plays, so it is the
 * visible row, not a single card:
 *   - Below 700px the count is THE MERCHANT'S OWN SETTING, `videos_per_row_mobile`, and that is
 *     a change worth stating plainly because this paragraph used to say "one at a time" and mean
 *     it. The phone width was a hard-coded 74vw, which fits one card and a sliver, so one played.
 *     It is a lookup now, and how many play is simply how many the chosen option shows. Measured
 *     on the live storefront at 430px: 1 plays at option "1" (74vw), 2 at the default "2" (40vw),
 *     3 at "3" (27vw) — and in every case the PEEKING card stays paused with `readyState 0` and
 *     nothing buffered, because a third of a card does not pass the half-of-what-can-be-seen rule.
 *     So the mobile-data concern still lives here, but the bound is now visible and adjustable
 *     rather than an accident of a width nobody could change: a merchant who wants one preview at
 *     a time on phones sets the option to 1. What keeps the cost argument above intact either way
 *     is unchanged — `preload="none"` plus visibility-gated playback means an off-screen or
 *     merely-peeking card downloads nothing at all.
 *   - At 1000px and up a card is at most `max_video_width` wide (240px by default), so the row
 *     holds as many as fit — about five on this store's 1400px row — and every one of them is
 *     fully visible, so they all play. That is the reference layout's look: a row of short,
 *     silent, looping previews.
 *
 *     THAT NUMBER IS NO LONGER `videos_per_row_desktop`, and the difference is worth stating
 *     because this paragraph used to give the count as the bound. Since the width cap landed, the
 *     count sets the widest a card may BE, not how many are VISIBLE — the visible number is
 *     (row + gap) / (card + gap) — so a product with five clips and a stored count of three now
 *     starts five previews, not three. Still bounded by one row, which is the whole cost argument,
 *     and still never by the eight videos on the product.
 * The difference from `autoplay` in the markup is therefore real and is about the BOUND: at most
 * one row on a broadband screen, versus every video on the product on a phone.
 *
 * REDUCED MOTION IS HONOURED AS A LIVE PREFERENCE, not as a one-off reading at startup: someone
 * who turns it on mid-session gets playback stopped, and someone who turns it off gets it back
 * without a reload.
 */
(() => {
  const SECTION = "[data-ss-autoplay]";
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  /** Every video currently observed, so a preference change can act on all of them at once. */
  const observed = new Set();

  const wants = () => !motionQuery.matches;

  /**
   * Videos whose audio the SHOPPER has taken charge of.
   *
   * `controls` is on deliberately, so unmuting is an expected interaction — and this script would
   * otherwise undo it. `play()` runs on every scroll back into view, and toggling reduced motion
   * off re-observes every card, so a force-mute on each start throws the shopper's choice away
   * repeatedly with no visible cause. We only ever SET `muted = true` and never clear it, so an
   * element seen unmuted can only have been unmuted by them — which makes this test reliable
   * without having to distinguish our own writes from theirs.
   */
  const shopperOwnsAudio = new WeakSet();
  const wired = new WeakSet();

  const watchAudio = (video) => {
    if (wired.has(video)) return;
    wired.add(video);
    video.addEventListener?.("volumechange", () => {
      if (!video.muted) shopperOwnsAudio.add(video);
      // The native controls are the other way the sound gets turned on — the reduced-motion and
      // no-observer paths keep them — so the button's label has to follow the element's state
      // rather than only its own clicks, or it ends up offering to do what has already been done.
      relabelSound(video);
    });
  };

  /**
   * Playback THIS SCRIPT started, and therefore the only playback it may stop.
   *
   * Without this, `stop()` pauses whatever is playing — including a video the shopper started
   * themselves on the native controls, which the very next scroll would take away with no visible
   * cause. That lands hardest on exactly the visitor the `controls` fallback exists for: someone
   * with reduced motion on, for whom NOTHING is ever script-started, so every video they play is
   * one this script would then pause. The audio path already reasons this way; so does this one.
   */
  const startedByUs = new WeakSet();

  /**
   * Cards whose browser REFUSED to autoplay them, even muted.
   *
   * Sticky for the session, on purpose, and never cleared. A browser that declined once (iOS Low
   * Power Mode, a data-saver profile) will decline the next scroll too, so re-testing on each
   * pass would take the controls away and hand them back over and over — a flickering player bar
   * over a frozen poster, which is worse than either state. The cost of being sticky is one card
   * keeping its native controls after the condition lifts; the cost of not being is a strip that
   * flickers for the whole visit.
   */
  const autoplayRefused = new WeakSet();

  /** Is this script actually driving this card, rather than merely intending to? */
  const driving = (video) => Boolean(observer) && wants() && !autoplayRefused.has(video);

  const play = (video) => {
    if (!wants()) return;
    // Muted is what makes autoplay permissible at all; setting it here as well as in the markup
    // means a merchant editing the section in the theme editor cannot accidentally produce a
    // strip that asks the browser for sound and is silently refused. The one exception is a
    // shopper who turned the sound on themselves — after that interaction the page has user
    // activation, so an unmuted start is normally allowed, and silently re-muting them would be
    // the more surprising outcome by far.
    if (!shopperOwnsAudio.has(video)) video.muted = true;
    startedByUs.add(video);
    // A rejected play() is normal — the tab is backgrounded, the element was removed, the browser
    // declined. It is not an error worth a console entry on a shopper's product page.
    //
    // BUT IT IS THE THIRD CASE WHERE THE CHROME HAS TO COME BACK. This file's own header calls a
    // still frame with no affordance the one outcome worse than visible chrome, and guards two
    // routes to it — no observer, and reduced motion. A browser that simply REFUSES muted
    // autoplay is a third: iOS Low Power Mode blocks it outright, and desktop data-saver profiles
    // can. The controls were already off by then and the rejection was swallowed, so the shopper
    // got a strip of frozen posters whose only recovery was an unadvertised tap that also turned
    // the sound on. Recording the refusal and re-running `chrome` hands the real player back.
    const attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch((error) => {
        // IT NEVER BECAME OURS. `startedByUs` is claimed optimistically above, because the
        // observer can call `stop()` before this promise settles and the claim has to already be
        // there for that to work. A rejection means the claim was wrong, so it is dropped —
        // otherwise a card the browser refused stays marked as script-started, and when the
        // shopper presses the restored native play button the next scroll pauses THEIR playback,
        // breaking the exact invariant `startedByUs` exists to protect.
        startedByUs.delete(video);

        // ONLY `NotAllowedError` MEANS "THE BROWSER REFUSED". Reading every rejection as a refusal
        // is wrong in the most common case by far: with `preload="none"` the play promise is
        // usually still pending when the card scrolls out, `stop()` calls `pause()`, and the spec
        // says that rejects the pending promise with `AbortError`. So one brisk scroll past the
        // strip would have marked every card refused — permanently, since the set is never
        // cleared — restoring the native controls, hiding the sound button and making the tap a
        // no-op for the rest of the session. That is the embedded-file look this change removes,
        // reinstated by scrolling.
        if (error && error.name === "NotAllowedError") {
          autoplayRefused.add(video);
          chrome(video);
        }
      });
    }
  };

  const stop = (video) => {
    // Only ever undo our own doing. Playback the shopper started stays theirs.
    if (!startedByUs.has(video)) return;
    startedByUs.delete(video);
    if (!video.paused) video.pause();
  };

  /**
   * "Enough of it is on screen to be worth playing."
   *
   * A BARE `threshold: 0.5` IS A BUG HERE, not a stricter version of this. A threshold is a
   * fraction of the CARD, so it is unreachable whenever the card is more than twice the size of
   * the viewport — the observer then never reports the element as intersecting and the video sits
   * on its poster frame no matter how the shopper scrolls. This strip reaches that case on a
   * common device, at a setting a merchant can pick: `videos_per_row_mobile` = 1 is a `74vw` card
   * in a 9:16 shape, i.e. ~1.32x the viewport WIDTH tall, so a phone held in landscape (667x375)
   * gets a ~494x878 card against a 375px-tall viewport and a best achievable ratio of ~0.43.
   * Autoplay would simply never happen there.
   *
   * THAT IS NO LONGER THE DEFAULT GEOMETRY, and the difference matters to anyone tempted to
   * simplify the list below. The phone width used to be a hard-coded `74vw`, so EVERY phone hit
   * the unreachable case sideways; since it became a setting the default is 2-per-row (`40vw`,
   * ~267x474, best ratio ~0.79) and only option "1" reproduces it. Checking the claim against the
   * default alone therefore makes the sampled list look like over-engineering — it is not, and the
   * contract suite derives BOTH geometries from this section's own lookup table so that a retune
   * of option "1" which lifts it back over 0.5 fails there rather than here.
   *
   * So the test is expressed against what CAN be seen rather than against the card: half of the
   * largest area that could possibly be visible. For a card smaller than the viewport that is
   * exactly the old "half the card" rule; for a card bigger than the viewport it becomes "the
   * card covers half the screen", which is the same intent at a size the fraction cannot express.
   */
  const visibleEnough = (entry) => {
    const box = entry.boundingClientRect;
    const seen = entry.intersectionRect;
    const root = entry.rootBounds;
    // `rootBounds` IS NULL IN A CROSS-ORIGIN BROWSING CONTEXT — which includes Shopify's own
    // theme-editor preview iframe, the one place a person is certain to be looking closely at
    // this. Falling back to a bare `intersectionRatio >= 0.5` there would reinstate exactly the
    // unreachable-threshold bug this function exists to remove, on the same tall-card geometry.
    // With no viewport to measure against, fall back in the direction that FAILS SAFE: play. The
    // videos are muted, looping and `preload="none"`, so starting one a little early costs a
    // preview nobody minds; never starting one is the defect.
    if (!box || !seen || !root) return Boolean(entry.isIntersecting);
    const reachable = Math.min(box.width, root.width) * Math.min(box.height, root.height);
    return reachable > 0 && seen.width * seen.height >= reachable * 0.5;
  };

  // Sampled rather than single-valued: the decision above is not a threshold, so the observer has
  // to be woken often enough to re-evaluate it. A card taller than the viewport spends its whole
  // journey inside a narrow band of ratios, and a sparse threshold list can be crossed once and
  // never again.
  const SAMPLES = Array.from({ length: 21 }, (_, i) => i / 20);

  const observer =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting && visibleEnough(entry)) play(entry.target);
              else stop(entry.target);
            }
          },
          { threshold: SAMPLES },
        )
      : null;

  /**
   * Turn this card's sound on or off.
   *
   * It toggles mute and nothing else — never play/pause, because visibility already owns that and
   * a tap that fought the observer would produce a video that stops when you touch it.
   *
   * REGISTERING THE PLAY AS OURS IS NOT OPTIONAL. `stop()` returns early for anything not in
   * `startedByUs`, so a card the observer had never started — one at the edge of the strip, or one
   * the shopper reached before it was visible enough — would be left playing OUT OF THE
   * OBSERVER'S REACH: it would keep looping, audibly, long after it scrolled off screen, and
   * nothing on the page could stop it. Starting it here is this script's own doing, so it is
   * recorded as such.
   *
   * `shopperOwnsAudio` is set HERE rather than left to `watchAudio`'s `volumechange`, which is
   * queued and has not run by the time `play()` is called — without it the play path would
   * immediately re-mute the video the shopper just unmuted.
   */
  const toggleSound = (video) => {
    // WHEREVER THE NATIVE CONTROLS ARE STILL ON SCREEN, THIS DOES NOTHING. No observer, reduced
    // motion, or a browser that refused to autoplay: in all three `chrome()` leaves the player
    // furniture in place and it owns the sound. A card-wide mute toggle underneath a visible
    // control bar would fight it, and clicking the video body to pause — the thing browsers do —
    // would silently change the volume instead. One predicate, shared with `chrome`, so the two
    // can never disagree about which state the card is in.
    if (!driving(video)) return;
    video.muted = !video.muted;
    if (!video.muted) {
      shopperOwnsAudio.add(video);
      startedByUs.add(video);
      const attempt = video.play();
      if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
    }
    relabelSound(video);
  };

  /**
   * The sound control that replaces the native one, built here rather than in the markup.
   *
   * ── WHY IT EXISTS ───────────────────────────────────────────────────────────────────────────
   *
   * Removing `controls` removes the element's ONLY keyboard and assistive-technology affordance: a
   * `<video>` without controls is not focusable, so a click listener on it is reachable by a mouse
   * or a finger and by nothing else. On the enhanced page — observer present, reduced motion off —
   * that left a keyboard or screen-reader user with no route to the audio at all, which the
   * reduced-motion branch does not cover because it is not the branch they are on.
   *
   * ── WHY IT IS CREATED IN SCRIPT ─────────────────────────────────────────────────────────────
   *
   * It is the exact counterpart of the removal: the same code path that takes the native control
   * away puts a real one back, so the degraded page — which keeps its native controls — never
   * renders a second, redundant button. Markup plus a `hidden` attribute would put it in the DOM
   * of a page whose script never runs.
   */
  const SOUND_BUTTON_CLASS = "ss-video-strip__sound";
  const soundButtons = new WeakMap();

  const speaker = (muted) =>
    muted
      ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="m23 9-6 6"/><path d="m17 9 6 6"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M19 5a9 9 0 0 1 0 14"/></svg>';

  function relabelSound(video) {
    const button = soundButtons.get(video);
    if (!button) return;
    // The label says what pressing it WILL DO, which is the thing a screen-reader user needs to
    // hear; the icon carries the current state visually and is hidden from the accessibility tree
    // so the two never contradict each other.
    button.setAttribute("aria-label", video.muted ? "Turn sound on" : "Turn sound off");
    button.innerHTML = speaker(video.muted);
  }

  const addSoundButton = (video) => {
    if (!observer || soundButtons.has(video)) return;
    const host = video.parentElement;
    if (!host) return;
    const button = document.createElement("button");
    button.type = "button";
    button.className = SOUND_BUTTON_CLASS;
    // Its own click must not also reach the card underneath, which toggles the same thing — two
    // toggles in one press is a button that appears to do nothing.
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      toggleSound(video);
    });
    host.appendChild(button);
    soundButtons.set(video, button);
    relabelSound(video);
  };
  /**
   * The player furniture, taken off ONLY once this script is actually driving playback.
   *
   * `controls` is in the markup as the no-JS fallback, and that fallback has to survive — see the
   * section's own comment. But when it survives visibly it is also the thing that makes the strip
   * look like an embedded file rather than the silent looping reel the reference layout is. So the
   * markup keeps the controls and this removes them, which is the progressive-enhancement order:
   * the degraded page is the one with the affordance, never the one without.
   *
   * The two cases where we must NOT remove them are exactly the two where we start nothing:
   * no `IntersectionObserver`, and reduced motion. Leaving a shopper a still frame with no way to
   * play it is the one outcome worse than visible chrome, so this is re-applied whenever the
   * preference changes rather than read once at startup.
   */
  const chrome = (video) => {
    if (!observer) return;
    // "Enhanced" means this script is ACTUALLY driving playback, which is a stronger claim than
    // "it intends to": a browser that refused the play is not being driven by anything, so it
    // keeps its controls exactly as the reduced-motion visitor does.
    const enhanced = driving(video);
    const button = soundButtons.get(video);
    if (enhanced) video.removeAttribute("controls");
    else video.setAttribute("controls", "controls");
    // EVERY AFFORDANCE MOVES TOGETHER, and the reason is that they are one control between them.
    // The replacement button and the card's tappable cursor are only true while the native chrome
    // is gone; left on through a reduced-motion switch they would offer a second sound control
    // beside the real one, and advertise a click the toggle above now refuses.
    if (button) button.hidden = !enhanced;
    video.parentElement?.classList.toggle("ss-is-tappable", enhanced);
  };


  const attach = (root) => {
    for (const video of root.querySelectorAll("video")) {
      watchAudio(video);
      if (observed.has(video)) continue;
      observed.add(video);
      addSoundButton(video);
      chrome(video);
      video.addEventListener("click", () => toggleSound(video));
      observer?.observe(video);
    }
  };

  const scan = (within) => {
    for (const strip of within.querySelectorAll?.(SECTION) ?? []) attach(strip);
    if (within.matches?.(SECTION)) attach(within);
  };

  scan(document);

  motionQuery.addEventListener("change", () => {
    // The chrome follows the preference in BOTH directions: turning reduced motion on hands the
    // controls back (we are about to stop starting anything), turning it off takes them away again.
    for (const video of observed) chrome(video);
    if (!wants()) {
      for (const video of observed) stop(video);
      return;
    }
    // TURNING THE PREFERENCE BACK OFF HAS TO RESTORE PLAYBACK, and simply doing nothing here does
    // not: an IntersectionObserver never re-emits for an element whose intersection has not
    // changed, so every card currently on screen would stay frozen until the shopper scrolled one
    // out and back in. Re-observing forces a fresh callback for each element, which starts exactly
    // the ones that are visible and leaves the rest alone.
    for (const video of observed) {
      observer?.unobserve(video);
      observer?.observe(video);
    }
  });

  // The theme editor tears a section down and rebuilds it on every settings change, which leaves
  // the observer watching detached elements and the new ones watched by nobody. Without this the
  // strip appears to "stop working" after one edit, in the one context where somebody is looking
  // closely at it.
  document.addEventListener("shopify:section:load", (event) => {
    for (const video of observed) {
      if (!video.isConnected) {
        observer?.unobserve(video);
        observed.delete(video);
      }
    }
    scan(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    for (const video of event.target.querySelectorAll?.("video") ?? []) {
      observer?.unobserve(video);
      observed.delete(video);
    }
  });
})();
